import type { BooleanFacetedFilterProps } from "./filters/BooleanFacetedFilter";
import type { DateFacetFilterProps } from "./filters/DateFacetedFilter";
import type { ListFacetedFilterProps } from "./filters/ListFacetedFilter";
import type { NumberFacetedFilterProps } from "./filters/NumberFacetedFilter";
import type { StringFacetedFilterProps } from "./filters/StringFacetedFilter";
import omit from "lodash/omit";
import { match } from "ts-pattern";
import { computed, defineComponent } from "vue";
import { $t } from "@/services/i18n";
import { p } from "@/utils/props-builder";
import Button from "../button/Button";
import Icon from "../icon/Icon";
import { injectTable } from "./DataTable";
import BooleanFacetedFilter from "./filters/BooleanFacetedFilter";
import DateFacetFilter from "./filters/DateFacetedFilter";
import ListFacetedFilter from "./filters/ListFacetedFilter";
import NumberFacetedFilter from "./filters/NumberFacetedFilter";
import StringFacetFilter from "./filters/StringFacetedFilter";

interface StringFilter extends StringFacetedFilterProps {
  _type: "string";
}

interface NumberFilter extends NumberFacetedFilterProps {
  _type: "number";
}

interface ListFilter extends ListFacetedFilterProps {
  _type: "list";
}

interface DateFilter extends DateFacetFilterProps {
  _type: "date";
}

interface BooleanFilter extends BooleanFacetedFilterProps {
  _type: "boolean";
}

export type FacetedFilter = StringFilter | NumberFilter | ListFilter | DateFilter | BooleanFilter;

export default defineComponent({
  name: "FacetedFilters",
  props: {
    filters: p.array<FacetedFilter[]>([]),
  },
  setup(props) {
    const table = injectTable();
    const hasColumnFilter = computed(() => {
      const state = table.getState();

      return !!state.columnFilters.length;
    });

    function resetFilters() {
      table.resetColumnFilters();
    }

    return () =>
      props.filters.length ? (
        <div class="center flex-wrap justify-start gap-2">
          {props.filters
            .slice()

            .map((filter) => {
              return match(filter)
                .with({ _type: "string" }, () => {
                  const props = omit(filter, "_type") as StringFacetedFilterProps;

                  return <StringFacetFilter key={filter.accessorKey} {...props} />;
                })
                .with({ _type: "number" }, () => {
                  const props = omit(filter, "_type") as NumberFacetedFilterProps;
                  return <NumberFacetedFilter key={filter.accessorKey} {...props} />;
                })
                .with({ _type: "list" }, () => {
                  const props = omit(filter, "_type") as ListFacetedFilterProps;
                  return <ListFacetedFilter key={filter.accessorKey} {...props} />;
                })
                .with({ _type: "date" }, () => {
                  const props = omit(filter, "_type") as DateFacetFilterProps;
                  return <DateFacetFilter key={filter.accessorKey} {...props} />;
                })
                .with({ _type: "boolean" }, () => {
                  const props = omit(filter, "_type") as BooleanFacetedFilterProps;
                  return <BooleanFacetedFilter key={filter.accessorKey} {...props} />;
                })
                .exhaustive();
            })}
          {hasColumnFilter.value && (
            <Button class="h-8" onClick={resetFilters} variant="subtle" muted>
              <span class="center gap-1">
                {$t("remove")}
                <Icon icon="lucide:x" class="-mr-1 h-4 w-4" />
              </span>
            </Button>
          )}
        </div>
      ) : (
        <div></div>
      );
  },
});
